<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import SquareImage from '@/components/SquareImage.vue'
import GalleryArrowIcon from '@/components/icons/GalleryArrowIcon.vue'

const props = defineProps({
  images: {
    type: Array,
    default: () => [
      { url: '/static/img/backstage_1.webp' },
      { url: '/static/img/backstage_2.webp' },
      { url: '/static/img/backstage_3.webp' },
      { url: '/static/img/backstage_4.webp' },
    ],
  },
  // Только для главной: обрезать края галереи по контейнеру
  clipEdges: {
    type: Boolean,
    default: false,
  },
})

const currentIndex = ref(0)
const isDesktop = ref(false)
const isHovered = ref(false)
const isTransitioning = ref(true)
let autoScrollInterval = null

// Проверяем размер экрана
const checkIsDesktop = () => {
  isDesktop.value = window.innerWidth > 1024 // 64rem = 1024px
}

// Показывать карусель если десктоп и больше 4 изображений
const shouldShowCarousel = computed(() => {
  return isDesktop.value && props.images.length > 4
})

// Отслеживаем изменения shouldShowCarousel
watch(shouldShowCarousel, (newVal, oldVal) => {
  if (newVal) {
    // Галерея переключилась в режим карусели (обычно после того, как с бэка пришло > 4 изображений)
    // Выставляем стартовый индекс в «середину» дублированного массива,
    // чтобы не было пустых первых элементов при первой отрисовке.
    if (!oldVal && props.images.length > 4) {
      currentIndex.value = props.images.length * 2 + 1
    }
    startAutoScroll()
  } else {
    stopAutoScroll()
  }
})

// Получаем изображения для отображения
const visibleImages = computed(() => {
  if (!shouldShowCarousel.value) {
    // На мобильных или если <= 4 изображений - показываем все
    return props.images
  }

  // На десктопе с каруселью - дублируем изображения для бесконечной прокрутки
  // Создаем массив с 10 копиями
  const copies = []
  for (let i = 0; i < 10; i++) {
    copies.push(...props.images)
  }
  return copies
})

// Вычисляем offset для transform
const slideOffset = computed(() => {
  if (!shouldShowCarousel.value) return 0
  // Каждый слайд = 25%
  // Вычитаем images.length * 2 чтобы показывать вторую копию как оригинал
  return -((currentIndex.value - props.images.length * 2) * 25.5)
})

// Определяем боковые изображения (для эффекта растворения)
const isSideImage = (index) => {
  if (!shouldShowCarousel.value) return false
  // Боковые изображения: те что за пределами основных 4
  // Левые боковые: currentIndex - 2, currentIndex - 1
  // Правые боковые: currentIndex + 4, currentIndex + 5
  const relativeIndex = index - currentIndex.value
  return relativeIndex === -2 || relativeIndex === -1 || relativeIndex === 4 || relativeIndex === 5
}

// Навигация
const nextSlide = () => {
  if (!shouldShowCarousel.value) return

  isTransitioning.value = true
  currentIndex.value++

  // Если достигли конца 8-й копии, перепрыгиваем на начало 2-й копии
  setTimeout(() => {
    if (currentIndex.value >= props.images.length * 8) {
      isTransitioning.value = false
      currentIndex.value = props.images.length * 2
      setTimeout(() => {
        isTransitioning.value = true
      }, 50)
    }
  }, 600) // После завершения анимации
}

const prevSlide = () => {
  if (!shouldShowCarousel.value) return

  isTransitioning.value = true
  currentIndex.value--

  // Если ушли ниже начала 2-й копии, перепрыгиваем на начало 8-й копии
  setTimeout(() => {
    if (currentIndex.value < props.images.length * 2) {
      isTransitioning.value = false
      currentIndex.value = props.images.length * 8
      setTimeout(() => {
        isTransitioning.value = true
      }, 50)
    }
  }, 600) // После завершения анимации
}

// Автопрокрутка
const startAutoScroll = () => {
  if (shouldShowCarousel.value && !autoScrollInterval && !isHovered.value) {
    autoScrollInterval = setInterval(() => {
      nextSlide()
    }, 4000)
  }
}

const stopAutoScroll = () => {
  if (autoScrollInterval) {
    clearInterval(autoScrollInterval)
    autoScrollInterval = null
  }
}

// Обработчики
const handleMouseEnter = () => {
  isHovered.value = true
  stopAutoScroll()
}

const handleMouseLeave = () => {
  isHovered.value = false
  startAutoScroll()
}

const handleResize = () => {
  checkIsDesktop()
  if (shouldShowCarousel.value) {
    startAutoScroll()
  } else {
    stopAutoScroll()
  }
}

onMounted(() => {
  checkIsDesktop()
  // Начинаем со второго фото второй копии для бесконечной прокрутки
  if (shouldShowCarousel.value && props.images.length > 4) {
    currentIndex.value = props.images.length * 2 + 1 // Второе фото второй копии
  }
  setTimeout(() => {
    if (shouldShowCarousel.value) {
      startAutoScroll()
    }
  }, 100)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  stopAutoScroll()
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div class="flex flex-center">
    <div
      class="image-gallery image-gallery--square-mobile"
      :class="{ 'image-gallery--clipped': props.clipEdges && shouldShowCarousel }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <!-- Кнопка назад -->
      <button
        v-if="shouldShowCarousel"
        class="gallery-arrow gallery-arrow--prev"
        @click="prevSlide"
        aria-label="Предыдущее изображение"
      >
        <GalleryArrowIcon />
      </button>

      <!-- Контейнер с изображениями -->
      <div class="gallery-wrapper">
        <div
          class="gallery-grid"
          :class="{
            'gallery-grid--carousel': shouldShowCarousel,
            'gallery-grid--transitioning': isTransitioning,
          }"
          :style="shouldShowCarousel ? { transform: `translateX(${slideOffset}%)` } : {}"
        >
          <div
            v-for="(image, index) in visibleImages"
            :key="`img-${index}-${image.url}`"
            class="gallery-item"
            :class="{
              'gallery-item--side': shouldShowCarousel && isSideImage(index),
            }"
          >
            <SquareImage style_main :image_url="image.url" />
          </div>
        </div>
        <!-- Gradient masks для эффекта растворения -->
        <div v-if="shouldShowCarousel" class="gallery-mask gallery-mask--left"></div>
        <div v-if="shouldShowCarousel" class="gallery-mask gallery-mask--right"></div>
        <!-- Белые блоки для полного перекрытия за пределами масок -->
        <div v-if="shouldShowCarousel" class="gallery-cover gallery-cover--left"></div>
        <div v-if="shouldShowCarousel" class="gallery-cover gallery-cover--right"></div>
      </div>

      <!-- Кнопка вперед -->
      <button
        v-if="shouldShowCarousel"
        class="gallery-arrow gallery-arrow--next"
        @click="nextSlide"
        aria-label="Следующее изображение"
      >
        <GalleryArrowIcon />
      </button>
    </div>
  </div>
</template>

<style scoped>
.image-gallery {
  width: 100%;
  max-width: 100%;
  position: relative;
  margin: 3.75rem 0;
}

.gallery-wrapper {
  width: 100%;
  overflow: visible;
  position: relative;
}

.image-gallery--clipped .gallery-wrapper {
  overflow: hidden;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, calc(25% - 0.84rem));
  gap: 1.25rem;
  width: 100%;
}

.gallery-grid--carousel {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc(25% - 0.84rem);
  gap: 1.25rem;
}

.gallery-grid--transitioning {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.gallery-item {
  transition: opacity 0.3s ease;
  position: relative;
}

.gallery-item--side {
  opacity: 0.6;
}

.gallery-mask {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20%;
  pointer-events: none;
  z-index: 5;
}

.gallery-mask--left {
  left: -20%;
  background: linear-gradient(to right, #fff 0%, transparent 80%);
}

.gallery-mask--right {
  right: -20%;
  background: linear-gradient(to left, #fff 0%, transparent 80%);
}

/* Белые блоки для полного перекрытия фото за пределами масок */
.gallery-cover {
  position: absolute;
  top: 0;
  bottom: 0;
  background: #fff;
  pointer-events: none;
  z-index: 10;
}

.gallery-cover--left {
  right: 120%;
  width: 100vw;
}

.gallery-cover--right {
  left: 120%;
  width: 100vw;
}

.gallery-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  z-index: 10;
  width: auto;
  height: auto;
}

.gallery-arrow:hover {
  opacity: 0.8;
}

.gallery-arrow:active {
  opacity: 0.6;
}

.gallery-arrow--prev {
  left: -5rem;
  transform: translateY(-50%) rotate(180deg);
}

.gallery-arrow--next {
  right: -5rem;
  transform: translateY(-50%) rotate(0);
}

@media (max-width: 96rem) {
  .gallery-arrow--prev {
    left: -4rem;
  }

  .gallery-arrow--next {
    right: -4rem;
  }
}

.gallery-arrow svg {
  display: block;
}

@media (max-width: 64rem) {
  .image-gallery {
    margin: 2.5rem 0;
  }

  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .gallery-arrow {
    display: none;
  }

  /* Квадратные превью на планшетах и мобильных (главная, автор, проект) */
  .image-gallery--square-mobile .gallery-item :deep(.square-image-root) {
    width: 100%;
    height: auto;
    max-height: none;
    aspect-ratio: 1 / 1;
    align-items: stretch;
  }

  .image-gallery--square-mobile .gallery-item :deep(.square-image-inner) {
    min-height: 0;
    height: 100%;
  }

  .image-gallery--square-mobile .gallery-item :deep(.image-square.square) {
    height: 100%;
    min-height: 0;
    max-height: none;
  }

  .image-gallery--square-mobile .gallery-item :deep(.card-mobile) {
    max-height: none;
  }
}

@media (max-width: 36rem) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}
</style>
